/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Service for managing user and group assignments via the Flask API.
 */
angular.module('nsadmin').factory('userAssignmentService', ['$injector',
    function userAssignmentService($injector) {

        var flaskApiService = $injector.get('flaskApiService');

        var service = {};

        // ==================== USER-LAB ASSIGNMENTS ====================

        /**
         * Gets all user-to-lab assignments.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of user-lab assignments.
         */
        service.getUserLabAssignments = function getUserLabAssignments() {
            return flaskApiService.get('/user-assignments/labs');
        };

        /**
         * Assigns a user directly to a lab.
         *
         * @param {String} userEmail
         *     The email of the user to assign.
         *
         * @param {String} labId
         *     The ID of the lab.
         *
         * @returns {Promise}
         *     A promise that resolves when the assignment is created.
         */
        service.assignUserToLab = function assignUserToLab(userEmail, labId) {
            return flaskApiService.post('/user-assignments/labs', {
                user_email: userEmail,
                lab_id: labId
            });
        };

        /**
         * Removes a user's direct assignment to a lab.
         *
         * @param {String} userEmail
         *     The email of the user.
         *
         * @param {String} labId
         *     The ID of the lab.
         *
         * @returns {Promise}
         *     A promise that resolves when the assignment is removed.
         */
        service.removeUserFromLab = function removeUserFromLab(userEmail, labId) {
            return flaskApiService.delete('/user-assignments/labs/' + userEmail + '/' + labId);
        };

        // ==================== USER-POD ASSIGNMENTS ====================

        /**
         * Gets all user-to-pod assignments.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of user-pod assignments.
         */
        service.getUserPodAssignments = function getUserPodAssignments() {
            return flaskApiService.get('/user-assignments/pods');
        };

        /**
         * Assigns a user directly to a pod.
         *
         * @param {String} userEmail
         *     The email of the user to assign.
         *
         * @param {String} podId
         *     The ID of the pod.
         *
         * @returns {Promise}
         *     A promise that resolves when the assignment is created.
         */
        service.assignUserToPod = function assignUserToPod(userEmail, podId) {
            return flaskApiService.post('/user-assignments/pods', {
                user_email: userEmail,
                pod_id: podId
            });
        };

        /**
         * Removes a user's direct assignment to a pod.
         *
         * @param {String} userEmail
         *     The email of the user.
         *
         * @param {String} podId
         *     The ID of the pod.
         *
         * @returns {Promise}
         *     A promise that resolves when the assignment is removed.
         */
        service.removeUserFromPod = function removeUserFromPod(userEmail, podId) {
            return flaskApiService.delete('/user-assignments/pods/' + userEmail + '/' + podId);
        };

        // ==================== GROUP-LAB MAPPINGS ====================

        /**
         * Gets all group-to-lab mappings.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of group-lab mappings.
         */
        service.getGroupLabMappings = function getGroupLabMappings() {
            return flaskApiService.get('/group-mappings/labs');
        };

        /**
         * Maps an Azure AD group to a lab.
         *
         * @param {String} groupId
         *     The Azure AD group ID.
         *
         * @param {String} labId
         *     The ID of the lab.
         *
         * @returns {Promise}
         *     A promise that resolves when the mapping is created.
         */
        service.mapGroupToLab = function mapGroupToLab(groupId, labId) {
            return flaskApiService.post('/group-mappings/labs', {
                group_id: groupId,
                lab_id: labId
            });
        };

        /**
         * Removes a group-to-lab mapping.
         *
         * @param {String} groupId
         *     The Azure AD group ID.
         *
         * @param {String} labId
         *     The ID of the lab.
         *
         * @returns {Promise}
         *     A promise that resolves when the mapping is removed.
         */
        service.unmapGroupFromLab = function unmapGroupFromLab(groupId, labId) {
            return flaskApiService.delete('/group-mappings/labs/' + groupId + '/' + labId);
        };

        // ==================== GROUP-POD MAPPINGS ====================

        /**
         * Gets all group-to-pod mappings.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of group-pod mappings.
         */
        service.getGroupPodMappings = function getGroupPodMappings() {
            return flaskApiService.get('/group-mappings/pods');
        };

        /**
         * Maps an Azure AD group to a pod.
         *
         * @param {String} groupId
         *     The Azure AD group ID.
         *
         * @param {String} podId
         *     The ID of the pod.
         *
         * @returns {Promise}
         *     A promise that resolves when the mapping is created.
         */
        service.mapGroupToPod = function mapGroupToPod(groupId, podId) {
            return flaskApiService.post('/group-mappings/pods', {
                group_id: groupId,
                pod_id: podId
            });
        };

        /**
         * Removes a group-to-pod mapping.
         *
         * @param {String} groupId
         *     The Azure AD group ID.
         *
         * @param {String} podId
         *     The ID of the pod.
         *
         * @returns {Promise}
         *     A promise that resolves when the mapping is removed.
         */
        service.unmapGroupFromPod = function unmapGroupFromPod(groupId, podId) {
            return flaskApiService.delete('/group-mappings/pods/' + groupId + '/' + podId);
        };

        // ==================== AZURE AD INTEGRATION ====================

        /**
         * Gets all available Azure AD groups.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of Azure AD groups.
         */
        service.getAzureAdGroups = function getAzureAdGroups() {
            return flaskApiService.get('/azure-ad/groups');
        };

        /**
         * Gets members of a specific Azure AD group.
         *
         * @param {String} groupId
         *     The Azure AD group ID.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of group members.
         */
        service.getGroupMembers = function getGroupMembers(groupId) {
            return flaskApiService.get('/azure-ad/groups/' + groupId + '/members');
        };

        /**
         * Adds a user to an Azure AD group.
         *
         * @param {String} groupId
         *     The Azure AD group ID.
         *
         * @param {String} userId
         *     The Azure AD user ID (object ID).
         *
         * @returns {Promise}
         *     A promise that resolves when the user is added.
         */
        service.addUserToAzureGroup = function addUserToAzureGroup(groupId, userId) {
            return flaskApiService.post('/azure-ad/groups/' + groupId + '/members', {
                user_id: userId
            });
        };

        /**
         * Removes a user from an Azure AD group.
         *
         * @param {String} groupId
         *     The Azure AD group ID.
         *
         * @param {String} userId
         *     The Azure AD user ID (object ID).
         *
         * @returns {Promise}
         *     A promise that resolves when the user is removed.
         */
        service.removeUserFromAzureGroup = function removeUserFromAzureGroup(groupId, userId) {
            return flaskApiService.delete('/azure-ad/groups/' + groupId + '/members/' + userId);
        };

        /**
         * Searches for Azure AD users.
         *
         * @param {String} searchTerm
         *     The search term (email or display name).
         *
         * @returns {Promise}
         *     A promise that resolves with an array of matching users.
         */
        service.searchAzureAdUsers = function searchAzureAdUsers(searchTerm) {
            return flaskApiService.get('/azure-ad/users/search?q=' + encodeURIComponent(searchTerm));
        };

        return service;
    }
]);
